(() => {
  if (window.VzyDigitalMotion) {
    window.VzyDigitalMotion.init();
    return;
  }

  const selector = '.title.js-anim-typewriter, .title.js-anim-rolling, .title.js-anim-appearing';
  const controllers = new Map();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const escapeHtml = (value) => value.replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));

  const decorate = (title) => {
    const visual = document.createElement('span');
    visual.setAttribute('aria-hidden', 'true');
    visual.innerHTML = title.getAttribute('data-content') || title.innerHTML;
    const walker = document.createTreeWalker(visual, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let length = 0;
    let node;
    while ((node = walker.nextNode())) {
      textNodes.push({ node, start: length, end: length + node.textContent.length });
      length += node.textContent.length;
    }
    const plain = visual.textContent;
    const matches = [...plain.matchAll(/\[([^\]]+)\]/g)];
    const groups = [];
    matches.reverse().forEach((match) => {
      const words = match[1].split('|').map((word) => word.trim()).filter(Boolean);
      if (!words.length) return;
      const start = textNodes.find((entry) => entry.start <= match.index && entry.end > match.index);
      const endOffset = match.index + match[0].length;
      const end = textNodes.find((entry) => entry.start < endOffset && entry.end >= endOffset);
      if (!start || !end) return;
      const range = document.createRange();
      range.setStart(start.node, match.index - start.start);
      range.setEnd(end.node, endOffset - end.start);
      const slot = document.createElement('span');
      slot.className = 'js-anim-title';
      Object.assign(slot.style, {
        display: title.classList.contains('title--word_break_an') ? 'grid' : 'inline-grid',
        gridTemplateColumns: 'minmax(0, 1fr)',
        position: 'relative',
        maxWidth: '100%',
        minWidth: '0',
        verticalAlign: 'bottom',
        overflow: 'hidden'
      });
      const wordNodes = words.map((word, index) => {
        const wordNode = document.createElement('span');
        wordNode.textContent = word;
        Object.assign(wordNode.style, { gridArea: '1 / 1', position: 'relative', display: 'block', width: 'auto', visibility: index ? 'hidden' : 'visible' });
        slot.append(wordNode);
        return wordNode;
      });
      range.deleteContents();
      range.insertNode(slot);
      range.detach();
      groups.unshift({ slot, words, wordNodes, writer: null, typed: null, finished: false });
    });
    const accessible = document.createElement('span');
    accessible.className = 'visually-hidden';
    accessible.textContent = plain.replace(/\[([^\]]+)\]/g, (_, words) => words.split('|')[0].trim());
    title.replaceChildren(accessible, visual);
    title.style.opacity = '1';
    return groups;
  };

  class MotionTitle {
    constructor(title) {
      this.title = title;
      this.original = title.innerHTML;
      this.opacity = title.style.opacity;
      this.mode = title.classList.contains('js-anim-typewriter') ? 'typewriter' : title.classList.contains('js-anim-rolling') ? 'rolling' : 'appearing';
      this.groups = decorate(title);
      this.visible = false;
      this.running = false;
      this.finished = false;
      this.destroyed = false;
      title.dataset.vzyNativeMotion = 'static';
    }

    showStatic() {
      this.timeline?.pause();
      this.groups.forEach((group) => {
        group.writer?.stop();
        if (group.typed) group.typed.style.visibility = 'hidden';
        group.wordNodes.forEach((word, index) => {
          word.style.visibility = index ? 'hidden' : 'visible';
          if (this.mode !== 'typewriter') {
            word.style.transform = 'none';
            word.style.opacity = '1';
          }
        });
      });
      this.running = false;
    }

    createTypewriters() {
      if (!window.Typewriter) return false;
      this.groups.forEach((group) => {
        if (group.writer) return;
        const typed = document.createElement('span');
        Object.assign(typed.style, { gridArea: '1 / 1', position: 'relative', display: 'block', minWidth: '0' });
        group.slot.append(typed);
        group.typed = typed;
        group.writer = new window.Typewriter(typed, { loop: false, delay: 110, deleteSpeed: 45, skipAddStyles: true });
        group.words.forEach((word, index) => {
          group.writer.typeString(escapeHtml(word)).pauseFor(500);
          if (index < group.words.length - 1) group.writer.deleteAll(45);
        });
        group.writer.callFunction(() => {
          group.finished = true;
          this.finished = this.groups.every((item) => item.finished);
          if (this.finished) {
            this.running = false;
            this.title.dataset.vzyNativeMotion = 'complete';
          }
        });
      });
      return true;
    }

    createTimeline() {
      if (this.timeline) return true;
      if (!window.gsap) return false;
      this.timeline = window.gsap.timeline({ paused: true, repeat: 0, onComplete: () => {
        this.finished = true;
        this.running = false;
        this.title.dataset.vzyNativeMotion = 'complete';
      } });
      this.groups.forEach((group) => {
        const words = group.wordNodes;
        words.forEach((word, index) => {
          window.gsap.set(word, this.mode === 'rolling' ? { yPercent: index ? 100 : 0, opacity: 1 } : { opacity: index ? 0 : 1 });
          word.style.visibility = 'visible';
        });
        if (words.length < 2) return;
        words.forEach((word, index) => {
          const next = words[(index + 1) % words.length];
          const at = 2 + index * 2.45;
          if (this.mode === 'rolling') {
            this.timeline.to(word, { yPercent: -100, duration: 0.45, ease: 'power2.inOut' }, at)
              .fromTo(next, { yPercent: 100 }, { yPercent: 0, duration: 0.45, ease: 'power2.inOut', immediateRender: false }, at)
              .set(word, { yPercent: 100 }, at + 0.45);
          } else {
            this.timeline.to(word, { opacity: 0, duration: 0.25 }, at)
              .to(next, { opacity: 1, duration: 0.25 }, at);
          }
        });
      });
      return true;
    }

    sync() {
      if (this.destroyed) return;
      if (motion.matches) {
        this.showStatic();
        this.title.dataset.vzyNativeMotion = 'reduced';
        return;
      }
      if (!this.visible || document.hidden) {
        this.timeline?.pause();
        this.groups.forEach((group) => group.writer?.stop());
        this.running = false;
        this.title.dataset.vzyNativeMotion = this.finished ? 'complete' : 'paused';
        return;
      }
      if (!this.groups.length || this.finished) {
        this.title.dataset.vzyNativeMotion = this.finished ? 'complete' : 'static';
        return;
      }
      if (this.running) return;
      if (this.mode === 'typewriter') {
        if (!this.createTypewriters()) return;
        this.groups.forEach((group) => {
          group.wordNodes.forEach((word) => { word.style.visibility = 'hidden'; });
          group.typed.style.visibility = 'visible';
          if (!group.finished) group.writer.start();
        });
      } else {
        if (!this.createTimeline()) return;
        if (this.wasReduced) {
          this.groups.forEach((group) => group.wordNodes.forEach((word, index) => {
            word.style.visibility = 'visible';
            window.gsap.set(word, this.mode === 'rolling' ? { yPercent: index ? 100 : 0, opacity: 1 } : { opacity: index ? 0 : 1 });
          }));
          this.timeline.invalidate().restart();
        } else this.timeline.play();
      }
      this.wasReduced = false;
      this.running = true;
      this.title.dataset.vzyNativeMotion = 'running';
    }

    destroy() {
      this.destroyed = true;
      this.timeline?.kill();
      this.groups.forEach((group) => group.writer?.stop());
      this.title.innerHTML = this.original;
      this.title.style.opacity = this.opacity;
      delete this.title.dataset.vzyNativeMotion;
    }
  }

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const controller = controllers.get(entry.target);
      if (!controller) return;
      controller.visible = entry.isIntersecting;
      controller.sync();
    });
  }) : null;

  const init = (scope = document) => {
    const titles = [...scope.querySelectorAll(selector)];
    if (scope.matches?.(selector)) titles.unshift(scope);
    titles.forEach((title) => {
      if (controllers.has(title) || title.closest('.shopify-section-group-header-group, .shopify-section-group-footer-group')) return;
      const controller = new MotionTitle(title);
      controllers.set(title, controller);
      if (observer) observer.observe(title);
      else controller.visible = true;
      controller.sync();
    });
  };
  const syncAll = () => controllers.forEach((controller) => controller.sync());
  const unload = (scope) => controllers.forEach((controller, title) => {
    if (scope !== title && !scope.contains(title)) return;
    observer?.unobserve(title);
    controller.destroy();
    controllers.delete(title);
  });

  motion.addEventListener('change', () => {
    controllers.forEach((controller) => { controller.wasReduced = motion.matches || controller.wasReduced; });
    syncAll();
  });
  document.addEventListener('visibilitychange', syncAll);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.target));
  window.addEventListener('load', syncAll, { once: true });
  window.addEventListener('pageshow', syncAll);
  window.addEventListener('pagehide', () => controllers.forEach((controller) => {
    controller.timeline?.pause();
    controller.groups.forEach((group) => group.writer?.stop());
    controller.running = false;
  }));
  window.VzyDigitalMotion = { init, unload, version: '1.0.0' };
  init();
})();
